'use client';

import { FormEvent, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  LoginButton,
  LoginLink,
  RequestDemoLink,
} from '@/app/(nyshex_login)/styled';
import { Box, Flex, Input, Spacing24 } from '@/keel';
import { TypographyHeadingH4, Spacing32 } from '@nyshex/keel/src/keel';
import { loginToApp, updateRollbar } from '@/app/shared/helpers';
import { GdprModal } from '@/app/shared/components/GdprModals/GdprModal';
import { LoginErrorAlert } from '@/app/shared/HelperComponents';
import { BAD_RESPONSE, LOGIN_RESULTS } from '@/app/shared/constants';
import {
  publishRequestDemoClick,
  publishSubmitLoginForm,
  publishViewPage,
} from '@/app/shared/publishEventHelpers';

const NyshexLogin = () => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [hasEmptyField, setHasEmptyField] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect_to');

  const invalidUsername = hasEmptyField && !loginData.username;
  const invalidPassword = hasEmptyField && !loginData.password;

  const handleLoginSubmit = async (
    e?: FormEvent<HTMLFormElement>,
    acceptTos = false
  ) => {
    if (e) e.preventDefault();
    publishSubmitLoginForm({
      whiteLabel: false,
    });
    if (loginError) setLoginError(false);
    if (!loginData.username || !loginData.password) {
      setHasEmptyField(true);
      setLoading(false);
      return;
    }
    if (hasEmptyField) setHasEmptyField(false);
    setLoading(true);
    try {
      const loginResult = await loginToApp(
        { ...loginData, acceptTos },
        redirect
      );

      if (loginResult === BAD_RESPONSE) {
        setShowConsentModal(false);
        setLoginError(true);
        setLoading(false);
        return;
      }

      if (loginResult === LOGIN_RESULTS.NEED_CONSENT) {
        setShowConsentModal(true);
        setLoading(false);
      } else {
        router.push(loginResult as string);
      }
    } catch {
      setShowConsentModal(false);
      setLoading(false);
    }
  };

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    property: 'username' | 'password'
  ) => {
    setLoginData({ ...loginData, [property]: e.target.value });
  };

  useEffect(() => {
    publishViewPage({ whiteLabel: false });
    updateRollbar();
  }, []);

  return (
    <>
      <Flex
        direction="column"
        gap={Spacing24}
        data-testid="nyshex_login"
        sx={{
          paddingBottom: loginError ? Spacing24 : Spacing32,
          ...TypographyHeadingH4,
        }}
      >
        Log In
        {loginError && <LoginErrorAlert />}
      </Flex>
      <Flex
        as="form"
        direction="column"
        gap="18px"
        onSubmit={handleLoginSubmit}
      >
        <Input
          size="lg"
          label="Username"
          onChange={(e) => handleInput(e, 'username')}
          value={loginData.username}
          testId="loginUsernameInput"
          invalid={invalidUsername}
          errorText={invalidUsername ? 'This field is required.' : ''}
        />
        <Input
          type="password"
          size="lg"
          label="Password"
          onChange={(e) => handleInput(e, 'password')}
          value={loginData.password}
          testId="loginPasswordInput"
          secondaryLabelLink={{
            text: 'Forgot?',
            onClick: () => router.push('/forgot-password'),
          }}
          invalid={invalidPassword}
          errorText={invalidPassword ? 'This field is required.' : ''}
        />
        <LoginButton
          content="Log In"
          type="submit"
          testId="loginSubmitButton"
          loading={loading}
        />
      </Flex>
      <RequestDemoLink>
        <Box>
          Do you want free access? Get started with NYSHEX{' '}
          <LoginLink
            href="https://nyshex.com/sign-up"
            target="_blank"
            passHref
            onClick={publishRequestDemoClick}
          >
            here.
          </LoginLink>
        </Box>
        <Box>
          Does your organization already have access? Request access{' '}
          <LoginLink
            href="mailto:customer.support@nyshex.com"
            target="_blank"
            passHref
            onClick={publishRequestDemoClick}
          >
            here.
          </LoginLink>
        </Box>
      </RequestDemoLink>
      {showConsentModal && (
        <GdprModal
          isOpen={showConsentModal}
          setIsOpen={setShowConsentModal}
          handleLogin={handleLoginSubmit}
        />
      )}
    </>
  );
};

export default NyshexLogin;
